import React, { useMemo, useRef } from "react";
import { Player, PlayerRef } from "@remotion/player";
import styled, { css } from "styled-components";
import { Link } from "react-router-dom";
import ResumeAdventure from "./composition/ResumeAdventure";
import useScrollFrame from "./useScrollFrame";
import Homepage from "../pages/homepage";
import {
  COMPOSITION_HEIGHT,
  COMPOSITION_WIDTH,
  DURATION_IN_FRAMES,
  FPS,
  PALO_BEAT_LENGTH,
  SCENES,
  SCROLL_HEIGHT_VH,
  SCROLL_HEIGHT_VH_MOBILE,
  sceneProgressRange,
} from "./composition/data/scenes";
import { CONTACT, NAME, ROLE, TAGLINE } from "./composition/data/resume";

const ScrollContainer = styled.div<{ $heightVh: number }>`
  height: ${(props) => props.$heightVh}vh;
  background-color: #181818;
`;

const StickyViewport = styled.div`
  position: sticky;
  top: 0;
  height: 100vh;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
`;

/* Cover-fit the 16:9 composition to any viewport (no letterboxing). */
const PlayerSizer = styled.div`
  flex: none;
  width: max(100vw, 177.78vh);
  height: max(100vh, 56.25vw);
`;

const overlayLinkStyles = css`
  pointer-events: auto;
  font-family: "JetBrains Mono", monospace;
  font-size: 18px;
  color: white;
  text-decoration: none;
  border: 2px solid #fcaeea;
  border-radius: 14px;
  padding: 10px 22px;
  background-color: rgba(24, 24, 24, 0.85);
  transition: background-color 0.2s ease;
  &:hover {
    background-color: #fcaeea;
    color: #181818;
  }
`;

const OverlayLink = styled.a`
  ${overlayLinkStyles}
`;

const OverlayRouterLink = styled(Link)`
  ${overlayLinkStyles}
`;

const OverlayLayer = styled.div`
  position: fixed;
  inset: 0;
  pointer-events: none;
  display: flex;
  align-items: flex-end;
  justify-content: center;
  padding-bottom: 12vh;
  gap: 18px;
`;

/* Selectable/crawlable mirror of the key copy for SEO + screen readers. */
const VisuallyHidden = styled.div`
  position: absolute;
  width: 1px;
  height: 1px;
  overflow: hidden;
  clip: rect(0 0 0 0);
  white-space: nowrap;
`;

const prefersReducedMotion = () =>
  typeof window !== "undefined" &&
  window.matchMedia("(prefers-reduced-motion: reduce)").matches;

const isMobileViewport = () =>
  typeof window !== "undefined" && window.innerWidth < 768;

// Progress window during which the First Rescue card is on stage (beat 2 of
// the Palo deck scene).
const FIRST_RESCUE_WINDOW: [number, number] = [
  (SCENES.paloDeck.from + PALO_BEAT_LENGTH) / DURATION_IN_FRAMES,
  (SCENES.paloDeck.from + 2 * PALO_BEAT_LENGTH) / DURATION_IN_FRAMES,
];

const AdventurePage: React.FC = () => {
  const playerRef = useRef<PlayerRef>(null);
  const progress = useScrollFrame(playerRef, DURATION_IN_FRAMES);

  const reducedMotion = useMemo(prefersReducedMotion, []);
  const heightVh = useMemo(
    () => (isMobileViewport() ? SCROLL_HEIGHT_VH_MOBILE : SCROLL_HEIGHT_VH),
    []
  );

  if (reducedMotion) {
    // Static fallback: the classic resume layout.
    return <Homepage />;
  }

  const [contactStart] = sceneProgressRange("contact");
  const showFirstRescue =
    progress >= FIRST_RESCUE_WINDOW[0] && progress <= FIRST_RESCUE_WINDOW[1];
  const showContact = progress >= contactStart;

  return (
    <ScrollContainer $heightVh={heightVh}>
      <StickyViewport>
        <PlayerSizer>
          <Player
            ref={playerRef}
            component={ResumeAdventure}
            durationInFrames={DURATION_IN_FRAMES}
            compositionWidth={COMPOSITION_WIDTH}
            compositionHeight={COMPOSITION_HEIGHT}
            fps={FPS}
            style={{ width: "100%", height: "100%" }}
            controls={false}
            clickToPlay={false}
            doubleClickToFullscreen={false}
            spaceKeyToPlayOrPause={false}
            initiallyMuted
          />
        </PlayerSizer>
      </StickyViewport>
      <OverlayLayer aria-hidden={!showFirstRescue && !showContact}>
        {showFirstRescue ? (
          <OverlayRouterLink to="/showcase/firstrescue">
            View the First Rescue showcase →
          </OverlayRouterLink>
        ) : null}
        {showContact ? (
          <>
            <OverlayLink href={`mailto:${CONTACT.email}`}>
              📮 {CONTACT.email}
            </OverlayLink>
            <OverlayLink
              href={CONTACT.linkedIn}
              target="_blank"
              rel="noreferrer"
            >
              in/ LinkedIn
            </OverlayLink>
            <OverlayRouterLink to="/classic">
              📄 classic resume view
            </OverlayRouterLink>
          </>
        ) : null}
      </OverlayLayer>
      <VisuallyHidden>
        <h1>{NAME}</h1>
        <p>{ROLE}</p>
        <p>{TAGLINE}</p>
        <p>
          Senior full stack TypeScript engineer and consultant. Experience:
          Zip Co, Palo IT (BookingSG, First Rescue/IAG, HSBC, Blocktrust,
          WhatTruck), 4Mation Technologies (QV/EGO AI chatbot, Montgomery
          Homes, Industrial Deafness Australia), Palo IT Senior Consultant
          (Vista agentic orchestrator, I-MED IMOGEN, Australian Payments
          Plus, UrbnSurf, Virgin Active). Full details on the classic page.
        </p>
        <a href="/classic">Classic resume layout</a>
      </VisuallyHidden>
    </ScrollContainer>
  );
};

export default AdventurePage;
